import React, { useState, useEffect, useRef } from 'react';
import DataGrid, {
    Column,
    ColumnChooser,
    Export,
    FilterRow,
    Grouping,
    GroupPanel,
    HeaderFilter,
    KeyboardNavigation,
    LoadPanel,
    MasterDetail,
    Paging,
    Scrolling,
    SearchPanel,
    Selection,
} from 'devextreme-react/data-grid';
import { Template } from 'devextreme-react/core/template';
import { DateFormat } from '../../helpers/fixcodes';
import { formatDate } from '../../helpers/globalfunctions';

// Formats a value as MM/dd/yyyy hh:mm only when it parses as a real date. Values such as
// flightATA arrive as free-form strings (e.g. just "14:35") which new Date() can't parse and
// would otherwise render as "NaN/NaN/NaN NaN:NaN", so those are shown as-is instead.
function formatCellDate(value) {
    if (value === null || value === undefined || String(value).trim() === '') {
        return '';
    }
    if (isNaN(new Date(value).getTime())) {
        return String(value).trim();
    }
    return formatDate(value, "MM/dd/yyyy hh:mm");
}

// Custom grid: Flight Time cell shows flightTime with flightTerminal on the line below it
// (rendered via the dx-cell-pre-line class). Using calculateCellValue rather than a cellRender
// keeps the combined text searchable (grid search and header-filter search both match any part
// of it, e.g. a terminal) and included in the Excel export.
function calculateFlightTimeCellValue(rowData) {
    return [
        rowData.flightTime,
        rowData.flightTerminal ? 'Terminal: ' + rowData.flightTerminal : null,
    ].filter(Boolean).join('\n');
}

// Custom grid: Reporting Time cell shows the debriefing time when there is one, otherwise the
// duty reporting time, prefixed "D : " / "R : " so the dispatcher can tell which it is.
function calculateReportingTimeCellValue(rowData) {
    const deBriefingTime = formatCellDate(rowData.deBriefingTime);
    if (deBriefingTime) {
        return 'D : ' + deBriefingTime;
    }
    const dutyReportingTime = formatCellDate(rowData.dutyReportingTime);
    if (dutyReportingTime) {
        return 'R : ' + dutyReportingTime;
    }
    return '';
}

// Sorts Reporting Time by the actual date it shows, rather than by its "D : " / "R : " text.
function calculateReportingTimeSortValue(rowData) {
    const value = rowData.deBriefingTime || rowData.dutyReportingTime;
    const time = value ? new Date(value).getTime() : NaN;
    return isNaN(time) ? null : time;
}

// Custom grid: shows the allowClubbing boolean as readable text.
function calculateAllowClubbingCellValue(rowData) {
    return rowData.allowClubbing === true ? 'Yes' : 'No';
}

// Custom grid: Clubbed No cell shows clubbedNo with the ride's pickup sequence in the club underneath
// ("Seq: n"). pickupPriority is 0 when not set, so no "Seq" line is shown for those rides.
function calculateClubbedNoCellValue(rowData) {
    const seq = Number(rowData.pickupPriority) > 0 ? 'Seq: ' + rowData.pickupPriority : null;
    return [rowData.clubbedNo, seq].filter(v => v !== null && v !== undefined && String(v).trim() !== '').join('\n');
}

// Sorts by Clubbed No, then by pickup sequence within the same club.
function calculateClubbedNoSortValue(rowData) {
    const clubbedNo = rowData.clubbedNo == null ? '' : String(rowData.clubbedNo).trim();
    const seq = Number(rowData.pickupPriority) > 0 ? String(rowData.pickupPriority).padStart(4, '0') : '9999';
    return clubbedNo ? clubbedNo + '|' + seq : '￿';
}

// Custom grid: Gender cell shows gender with the crew's paxType underneath.
function calculateGenderCellValue(rowData) {
    return [rowData.gender, rowData.paxType].filter(Boolean).join('\n');
}

// Renders a "\n"-joined calculateCellValue as a primary line plus smaller, muted secondary line(s).
// Display only: search, header filter and export all keep using the calculated text itself.
function renderStackedCell(cellInfo) {
    const [primary, ...secondary] = String(cellInfo.value || '').split('\n');
    return (
        <div style={{ lineHeight: 1.3 }}>
            <div style={{ fontWeight: 500 }}>{primary}</div>
            {secondary.map((line, i) => (
                <div key={i} style={{ color: '#6c757d', fontSize: '1em' }}>{line}</div>
            ))}
        </div>
    );
}

// Vehicle/driver/Assign-Unassign row rendered above the grid (see toolbarItemRender below) —
// flex-shrink: 0 keeps it its natural height inside the flex-column wrapper regardless of
// how many lines its Bootstrap row/col content wraps onto at narrower widths.
const TOOLBAR_EXTRA_STYLE = {
    flexShrink: 0,
    padding: '8px 6px',
    marginBottom: 6,
    borderBottom: '1px solid #dde0e5',
};

const DispatchGrid = React.memo(React.forwardRef(function DispatchGrid({
    variant,
    dataSource,
    searchPanelText,
    selectedRowKeys,
    enterKeyAction,
    allowToAddDriverWorkingHours,
    onToolbarPreparing,
    onRowPrepared,
    onSelectionChanged,
    onOptionChanged,
    onExporting,
    onCellPrepared,
    renderDetail,
    toolbarItemRender,
    // Renders the Pickup Time / Crew Type filters injected into the custom grid's toolbar.
    // The parent also passes pickupTimeSlotId/crewTypeId props (not read here) so this memoized
    // component re-renders, and the toolbar template shows the new selection, when they change.
    renderFilterToolbarItems,
    renderReportingDeBriefingCell,
    renderGridCell,
    renderDeBriefingCell,
    renderPickupTimeCell,
    renderPickupAddressCell,
    renderDropoffAddressCell,
    renderFlightInfoCell,
    renderJobIdCell,
    renderPaxNameCell,
    socketDataUpdated,
}, ref) {
    const wrapperRef = useRef(null);
    const toolbarRef = useRef(null);
    const [gridHeight, setGridHeight] = useState(500);

    useEffect(() => {
        const update = () => {
            if (!wrapperRef.current) return;
            const { top } = wrapperRef.current.getBoundingClientRect();
            // The custom vehicle/driver/Assign-Unassign row now renders above the grid
            // (instead of inside the DevExtreme toolbar), so its height — which varies as
            // it wraps at narrower widths — has to come out of the grid's own height too.
            const toolbarHeight = toolbarRef.current ? toolbarRef.current.getBoundingClientRect().height : 0;
            setGridHeight(Math.max(300, window.innerHeight - top - toolbarHeight - 4));
        };
        update();
        window.addEventListener('resize', update);
        const resizeObserver = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(update) : null;
        if (resizeObserver && toolbarRef.current) {
            resizeObserver.observe(toolbarRef.current);
        }
        return () => {
            window.removeEventListener('resize', update);
            if (resizeObserver) resizeObserver.disconnect();
        };
    }, []);

    //if (variant === 'standard') {
    //    return (
    //        <div ref={wrapperRef} style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
    //            {toolbarItemRender && <div ref={toolbarRef} className="dispatch-toolbar-extra" style={TOOLBAR_EXTRA_STYLE}>{toolbarItemRender()}</div>}
    //            <DataGrid
    //                ref={ref}
    //                id="dispatch-grid-container"
    //                name="gridDispatchedContainer"
    //                dataSource={dataSource}
    //                className="dx-datagrid-height"
    //                height={gridHeight}
    //                columnsAutoWidth={true}
    //                showColumnLines={true}
    //                showRowLines={true}
    //                showBorders={true}
    //                rowAlternationEnabled={false}
    //                allowColumnResizing={true}
    //                columnResizingMode='widget'
    //                onToolbarPreparing={onToolbarPreparing}
    //                onRowPrepared={onRowPrepared}
    //                selectedRowKeys={selectedRowKeys}
    //                onSelectionChanged={onSelectionChanged}
    //                onOptionChanged={onOptionChanged}
    //            >
    //                <KeyboardNavigation enterKeyAction={enterKeyAction} />
    //                <MasterDetail enabled={true} autoExpandAll={false} render={renderDetail} />
    //                <Column dataField="pickupDateTime" caption="Pick-up Time" width={120} allowFiltering={false} allowSorting={true} format={DateFormat.DateTime24Format} dataType="datetime" headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="driverName" caption="Driver Name" width={150} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="driverMobileNo" caption="Driver Mobile" width={100} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="driverWorkingHours" caption="Driver working Hrs" width={120} allowFiltering={true} allowSorting={true} visible={allowToAddDriverWorkingHours === "true"} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="flightInfo" caption="Flight #" width={100} allowFiltering={true} allowSorting={true} cellRender={renderFlightInfoCell} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="puStreet" caption="Pick-up" width={200} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="doStreet" caption="Drop Off" width={200} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="vehicleNo" caption="Vehicle #" width={100} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="vehicleTypeName" caption="V Type" width={80} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="paxName" caption="Pax Name" width={150} allowFiltering={true} allowSorting={true} cellRender={renderPaxNameCell} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="rideId" caption="Ride Id" width="auto" dataType="string" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
    //                <Column dataField="driverUserName" caption="Driver #" width={100} visible={false} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="noOfAdults" caption="Pax" width={70} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="tripStatusDesc" caption="Pax Status" width={150} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="" width={90} caption="ETA/ETD" allowFiltering={false} allowSorting={false} cellRender={renderGridCell} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="flightStatus" caption="Flight Status" width={100} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="flightTime" caption="Flight Time" width={80} allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="flightEST" caption="Flight Est. Time" width={150} allowFiltering={true} allowSorting={true} visible={false} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="flightATA" caption="Flight Actual Time" width={150} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="flightTerminal" caption="Flight Term." width={100} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="flightGate" caption="Flight Gate" width={100} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="permanentRemark" caption="Note" width={150} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="specialInsruction" caption="Dispatch Note" width={150} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="dispatcherName" caption="Dispatcher" width={100} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="noOfCharter" caption="Charter" width={80} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="preAssignedAcknowledgeStatus" caption="PreAssigned Status" width={80} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="assignedTime" caption="Assign Time" width={100} allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="onLocationTime" caption="On Site" width={80} allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="onLoadTime" caption="Load Time" width={80} allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="onCompletedTime" caption="Completion Time" width={125} allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <Column dataField="meetGreetName" caption="Greeter Name" width={120} allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
    //                <ColumnChooser enabled={false} />
    //                <Grouping autoExpandAll={true} />
    //                <HeaderFilter visible={true} />
    //                <SearchPanel visible={true} text={searchPanelText} highlightCaseSensitive={false} highlightSearchResults={true} />
    //                <Export visible={true} enabled={true} allowExportSelectedData={true} />
    //                <GroupPanel visible={false} />
    //                <LoadPanel enabled={false} />
    //                <Scrolling mode="virtual" rowRenderingMode="virtual" showScrollbar="always" />
    //                <Paging enabled={false} />
    //                <Selection mode="multiple" selectAllMode="allPages" showCheckBoxesMode="always" />
    //            </DataGrid>
    //        </div>
    //    );
    //}

    return (
        <div ref={wrapperRef} style={{ flex: '1 1 auto', minHeight: 0, display: 'flex', flexDirection: 'column' }}>
            {toolbarItemRender && <div ref={toolbarRef} className="dispatch-toolbar-extra" style={TOOLBAR_EXTRA_STYLE}>{toolbarItemRender()}</div>}
            <DataGrid
                ref={ref}
                id="dispatch-grid-container-custom"
                name="gridDispatchedContainer"
                dataSource={dataSource}
                className="dx-datagrid-height"
                height={gridHeight}
                columnsAutoHeight={true}
                columnsAutoWidth={true}
                showColumnLines={true}
                showRowLines={true}
                showBorders={true}
                rowAlternationEnabled={false}
                allowColumnResizing={true}
                columnResizingMode='widget'                
                onToolbarPreparing={onToolbarPreparing}
                onRowPrepared={onRowPrepared}
                selectedRowKeys={selectedRowKeys}
                onSelectionChanged={onSelectionChanged}
                onOptionChanged={onOptionChanged}
                onExporting={onExporting}
                onCellPrepared={onCellPrepared}
            >
                {renderFilterToolbarItems && <Template name="dispatchFilterItems" render={renderFilterToolbarItems} />}
                <KeyboardNavigation enterKeyAction={enterKeyAction} />
                <MasterDetail enabled={true} autoExpandAll={false} render={renderDetail} />
                <Column dataField="flightInfo" caption="Flight #" width="auto" minWidth="100" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }} cellRender={renderFlightInfoCell} />
                {/*<Column dataField="flightTerminal" caption="Terminal" width="auto" minWidth="100" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }} />*/}
                {/* No dataField on purpose (same as Reporting Time below): with dataField set, the header filter
                    matches against the raw flightTime instead of the combined Flight Time / Terminal / Actual text. */}
                <Column name="flightTime" caption="Flight Time" width="auto" minWidth="130" dataType="string" allowFiltering={false} allowHeaderFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true, searchMode: 'contains' }} calculateCellValue={calculateFlightTimeCellValue} calculateSortValue="flightTime" cssClass="dx-cell-pre-line"/>
                {/*<Column dataField="flightATA" caption="Flight Actual Time" width="auto" minWidth="100" allowFiltering={false} allowSorting={true} format={DateFormat.DateTime24Format} dataType="datetime" headerFilter={{ allowSearch: true }}/>*/}
                {/* No dataField on purpose: with dataField set, filtering (incl. the header filter) runs against the raw
                    dutyReportingTime value instead of the calculated "D : ..." / "R : ..." text, so no rows ever match.
                    cellRender={renderReportingDeBriefingCell} */}
                <Column name="dutyReportingTime" caption="DeBriedf / Report" width="auto" minWidth="150" dataType="string" allowFiltering={false} allowHeaderFiltering={true} allowSorting={true} calculateCellValue={calculateReportingTimeCellValue} calculateSortValue={calculateReportingTimeSortValue} headerFilter={{ allowSearch: true, searchMode: 'contains' }} />
                {/*<Column dataField="deBriefingTime" caption="DeBriedfing Time" width={120} allowFiltering={false} allowSorting={true} cellRender={renderDeBriefingCell} headerFilter={{ allowSearch: true }}*/}
                <Column dataField="pickupDateTime" caption="Pick-up Time" width="auto" minWidth="130"  allowFiltering={false} allowSorting={true} format={DateFormat.DateTime24Format} dataType="datetime" cellRender={renderPickupTimeCell} headerFilter={{ allowSearch: true }}/>
                {/*<Column dataField="activityCode" caption="Activity Code" width={150} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }} />*/}
                {/* No dataField on purpose (same as Flight Time): the header filter and its search must match the
                    combined Clubbed No / "Seq: n" text, so either value can be filtered on. */}
                <Column name="clubbedNo" caption="Clubbed No" width="auto" minWidth="120" dataType="string" allowFiltering={true} allowHeaderFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true, searchMode: 'contains' }} calculateCellValue={calculateClubbedNoCellValue} calculateSortValue={calculateClubbedNoSortValue} cellRender={renderStackedCell} />
                {/*<Column dataField="clubbedStatus" caption="Clubbing Status" width="auto" minWidth="120" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }} />*/}
                <Column dataField="paxName" caption="Crew Name" width="auto" minWidth="120" allowFiltering={true} allowSorting={true} cellRender={renderPaxNameCell} headerFilter={{ allowSearch: true }} />
                {/* No dataField on purpose (same as Flight Time): the header filter must match the combined Gender / paxType text. */}
                <Column name="gender" caption="Gender" width="auto" minWidth="90" dataType="string" allowFiltering={true} allowHeaderFiltering={true} allowSearch={true} allowSorting={true} headerFilter={{ allowSearch: true, searchMode: 'contains' }} calculateCellValue={calculateGenderCellValue} calculateSortValue="gender" cellRender={renderStackedCell} />
                <Column dataField="paxMobileNo" caption="Crew Mobile" width="auto" minWidth="130" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }} />
                <Column dataField="paxType" caption="Rank" width="auto" minWidth="50" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                <Column dataField="rideType" caption="Trip Type" width="auto" minWidth="120" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }} />
                <Column dataField="tripStatusDesc" caption="Ride Status" width="auto" minWidth="120" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }} />
                <Column dataField="puStreet" caption="Pick-up" width="300" minWidth="300" allowFiltering={true} allowSorting={true} cellRender={renderPickupAddressCell} headerFilter={{ allowSearch: true }}/>
                <Column dataField="doStreet" caption="Drop Off" width="300" minWidth="300" allowFiltering={true} allowSorting={true} cellRender={renderDropoffAddressCell} headerFilter={{ allowSearch: true }} />
                <Column dataField="driverName" caption="Driver Name" width={150} allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }} />
                <Column dataField="vehicleNo" caption="Vehicle #" width="auto" minWidth="100" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                {/*<Column dataField="jobId" caption="Job No" dataType ="string" width={100} allowFiltering={true} allowSorting={true} cellRender={renderJobIdCell} headerFilter={{ allowSearch: true }}/>*/}
                {/* No dataField on purpose (same as Flight Time): the header filter must match the "Clubbed" / "Not Clubbed" text, not the raw boolean. */}
                <Column name="allowClubbing" caption="Allow Clubbing" width="auto" minWidth="140" dataType="string" allowFiltering={true} allowHeaderFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true, searchMode: 'contains' }} calculateCellValue={calculateAllowClubbingCellValue} calculateSortValue="allowClubbing"/>
                <Column dataField="rideConfirmed" caption="Ride Confirmation" width="auto" minWidth="120" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                <Column dataField="otpVerifiedStatus" caption="OTP Verify Status" width="auto" minWidth="120" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }} />                
                <Column dataField="driverWorkingHours" caption="Driver working Hrs" width="auto" minWidth="120" dataType = "string" allowFiltering={true} allowSorting={true} visible={allowToAddDriverWorkingHours === "true"} headerFilter={{ allowSearch: true }}/>
                <Column dataField="cancelledDate" caption="Cancellation date" width="auto" minWidth="100" allowFiltering={true} allowSorting={true} format={DateFormat.DateTime24Format} dataType="datetime" headerFilter={{ allowSearch: true }}/>
                <Column dataField="dispatcherName" caption="Dispatcher" width="auto" minWidth="100" allowFiltering={true} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                <Column dataField="assignedTime" caption="Assign Time" width="auto" minWidth="100" allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                <Column dataField="onLocationTime" caption="On Location" width="auto" minWidth="100" allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                <Column dataField="onLoadTime" caption="Boarded" width="auto" minWidth="100" allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                <Column dataField="onUnloadTime" caption="Departed" width="auto" minWidth="100" allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                <Column dataField="onCompletedTime" caption="Completed" width="auto" minWidth="100" allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                <Column dataField="assignedStatus" caption="Assigned Status" width="auto" minWidth="150" allowFiltering={false} allowSorting={true} headerFilter={{ allowSearch: true }}/>
                <Column dataField="rideId" caption="Ride Id" width="auto" minWidth="150"dataType="string" allowFiltering={true} allowSearch={true} headerFilter={{ allowSearch: true }} />
                <ColumnChooser enabled={false} />
                <Grouping autoExpandAll={true} />
                <HeaderFilter visible={true} />
                <SearchPanel visible={true} highlightCaseSensitive={true} width={240} placeholder="Search..." />
                <Export visible={true} enabled={true} allowExportSelectedData={true} fileName="Dispatch" />
                <GroupPanel visible={false} />
                <LoadPanel enabled={false} />
                <Scrolling mode="virtual" rowRenderingMode="virtual" showScrollbar="always" />
                <Paging enabled={false} />
                <Selection mode="multiple" selectAllMode="allPages" showCheckBoxesMode="always" allowSelectAll={false} />
            </DataGrid>
        </div>
    );
}));

export default DispatchGrid;
